import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Volume2, VolumeX } from 'lucide-react';

const Hero = ({ videoUrl, posterUrl, videoMobileUrl, posterMobileUrl, teamName, logo }) => {
  const videoRef = useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Telefono/tablet in verticale: video verticale leggero. Altrimenti video originale.
  const isMobile =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 1024px) and (orientation: portrait)').matches;
  const videoSrc = isMobile && videoMobileUrl ? videoMobileUrl : videoUrl;
  const posterSrc = isMobile && posterMobileUrl ? posterMobileUrl : posterUrl;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setIsVideoLoaded(true);
      // Start playing muted (browsers allow this)
      video.muted = true;
      video.play()
        .then(() => {
          setIsPlaying(true);
          console.log('Video playing');
        })
        .catch(err => console.log('Autoplay error:', err));
    };

    const handleCanPlay = () => {
      setIsVideoLoaded(true);
    };

    // Se il video non e' pronto entro 1,5 s (rete lenta, iPhone, risparmio dati)
    // si mostra comunque la pagina con l'immagine di anteprima.
    const fallbackTimer = setTimeout(() => setIsVideoLoaded(true), 1500);

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    
    return () => {
      clearTimeout(fallbackTimer);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, []);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      // Unmute and fade in audio
      video.muted = false;
      let currentVolume = 0;
      video.volume = 0;
      
      const fadeAudioInterval = setInterval(() => {
        if (currentVolume < 1) {
          currentVolume += 0.02;
          video.volume = Math.min(currentVolume, 1);
        } else {
          clearInterval(fadeAudioInterval);
        }
      }, 60); // 60ms * 50 steps = 3 seconds
      
      setIsMuted(false);
    } else {
      // Mute
      video.muted = true;
      setIsMuted(true);
    }
  };

  const scrollToContent = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative w-full h-screen overflow-hidden">
      {/* Video Background */}
      <video
        ref={videoRef}
        className="absolute top-0 left-0 w-full h-full object-cover"
        loop
        playsInline
        muted
        autoPlay
        poster={posterSrc}
        preload={isMobile ? 'metadata' : 'auto'}
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      
      {/* Audio Control Button */}
      {isPlaying && (
        <button
          onClick={toggleMute}
          className="absolute top-24 right-6 z-20 bg-black/50 backdrop-blur-sm hover:bg-cyan-500/80 text-white p-3 rounded-full transition-all duration-300 hover:scale-110"
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <VolumeX className="w-6 h-6" />
          ) : (
            <Volume2 className="w-6 h-6" />
          )}
        </button>
      )}

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>

      {/* Content */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-white px-4">
        {/* CTA Section - Minimal */}
        <div className="flex flex-col items-center gap-3">
          <button 
            onClick={() => document.getElementById('riders').scrollIntoView({ behavior: 'smooth' })}
            className="px-6 py-2 bg-white/10 backdrop-blur-sm hover:bg-cyan-500/20 text-white font-semibold text-sm rounded-lg border border-white/30 hover:border-cyan-400/50 transition-all duration-300"
          >
            I Nostri Piloti
          </button>
        </div>

        {/* Logo TM Bottom Center - Clickable */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
          <div className="relative group">
            <button
              onClick={scrollToContent}
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              className="relative transform hover:scale-105 transition-transform duration-300 focus:outline-none"
            >
              <img 
                src={logo} 
                alt="Team Logo" 
                className="w-24 h-auto drop-shadow-2xl"
              />
              
              {/* Overlay con testo "Scopri il Team" - 3 righe */}
              <div className={`absolute inset-0 bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center transition-opacity duration-300 ${showTooltip ? 'opacity-100' : 'opacity-0'}`}>
                <div className="text-cyan-400 font-bold text-sm leading-snug text-center">
                  <div>Scopri</div>
                  <div>il</div>
                  <div>Team</div>
                </div>
              </div>
            </button>
          </div>
          
          {/* Freccia sotto il logo */}
          <div className="animate-bounce">
            <ChevronDown className="w-6 h-6 text-cyan-400" />
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {!isVideoLoaded && (
        <div className="absolute inset-0 bg-black z-20 flex items-center justify-center">
          <div className="text-white text-2xl font-bold">Caricamento...</div>
        </div>
      )}
    </div>
  );
};

export default Hero;
